import type { SimpleChange, SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, Component, Input } from '@angular/core';
import type { FormGroup } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';

import { CurrentUserSelectors } from '@recruitee/account-data';
import type { User } from '@recruitee/account-types';
import { filterBoolean } from '@recruitee/common';
import { InfoboxComponent } from '@recruitee/components/infobox';
import { TranslatePipe } from '@recruitee/i18n';
import { InfoboxVideoComponent } from '@recruitee/onboarding-lib';
import { OnboardingVideoId } from '@recruitee/onboarding-lib-data';
import { UpdateContext } from '@recruitee/requisitions-data';
import type {
  RequisitionOpening,
  RequisitionDetailsFormFields,
  RequisitionAssignedFlow,
  Requisition,
} from '@recruitee/requisitions-types';
import { RequisitionStatus, DecisionStatus } from '@recruitee/requisitions-types';
import type { SelectOption } from '@recruitee/user-interface';
import { ProtectedControlDirective } from '@recruitee/user-interface';
import { deepEqual } from '@recruitee/utils';

import { ApprovalRequestMessageBoxComponent } from '../../../components/details/approval-request-message-box/approval-request-message-box.component';
import { RequisitionsContextFormComponent } from '../../../components/details/requisitions-context-form/requisitions-context-form.component';
import { RequisitionsDetailsFormComponent } from '../../../components/details/requisitions-details-form/requisitions-details-form.component';
import { RequisitionsLocationFormComponent } from '../../../components/details/requisitions-location-form/requisitions-location-form.component';
import { RequisitionsOpeningsFormComponent } from '../../../components/details/requisitions-openings-form/requisitions-openings-form.component';
import { RequisitionsToolbarComponent } from '../../../components/details/requisitions-toolbar/requisitions-toolbar.component';
import { RequisitionsWorkModelComponent } from '../../../components/details/requisitions-work-model/requisitions-work-model.component';
import { RejectionInfoboxComponent } from '../../../components/rejection-infobox/rejection-infobox.component';
import { CreateJobFacade } from '../../../facades/single-requisition/create-job.facade';
import type { RequestApprovalDisabled } from '../../../facades/single-requisition/requisitions-general.facade';
import { RequisitionsGeneralFacade } from '../../../facades/single-requisition/requisitions-general.facade';
import type { AvailableFieldsMap, RequisitionForm } from '../../../requisitions.types';
import { FormBox } from '../../../requisitions.types';
import { RequisitionFormDataAdapter } from '../../../services/requisition-form-data.adapter';
import { RequisitionFormManagerService } from '../../../services/requisition-form-manager.service';
import { RequisitionSaveService } from '../../../services/requisition-save.service';
import { RequisitionsNotificationsService } from '../../../services/requisitions-notifications.service';
import { RequisitionsSharedActionsService } from '../../../services/requisitions-shared-actions.service';
import { SingleRequisitionsActionsService } from '../../../services/single-requisitions-actions.service';

@Component({
  selector: 'rt-requisitions-details-container',
  templateUrl: './requisitions-details-container.component.html',
  styleUrls: ['./requisitions-details-container.component.less'],
  imports: [
    ApprovalRequestMessageBoxComponent,
    ProtectedControlDirective,
    RejectionInfoboxComponent,
    RequisitionsToolbarComponent,
    RequisitionsContextFormComponent,
    RequisitionsDetailsFormComponent,
    RequisitionsLocationFormComponent,
    RequisitionsWorkModelComponent,
    RequisitionsOpeningsFormComponent,
    RouterModule,
    TranslatePipe,
    InfoboxComponent,
    InfoboxVideoComponent,
  ],
})
export class RequisitionsDetailsContainerComponent {
  public readonly FormBox: typeof FormBox = FormBox;
  public readonly RequisitionStatus: typeof RequisitionStatus = RequisitionStatus;
  public readonly OnboardingVideoId: typeof OnboardingVideoId = OnboardingVideoId;
  public readonly featureDiscoveryKey = 'requisitionsInfoboxVideoUsed';

  @Input() public requisition: Requisition;
  @Input() public adminsMap: Record<number, User>;
  @Input() public approvalFlow: RequisitionAssignedFlow | null;
  @Input() public allApproversDeleted: boolean;
  @Input() public approversWithoutAccessCount: number;
  @Input() public isDepartmentsMode: boolean;

  public showFeatureDiscoveryVideoInfobox: boolean = false;
  public departmentSelectOptions: SelectOption[] = [];
  public isFormEditable = false;
  public availableFieldsMap: AvailableFieldsMap;
  public requestApprovalDisabled: Observable<RequestApprovalDisabled>;
  public savedOpenings: RequisitionOpening[];
  public deletedOpeningsCandidatesCount: number;
  public canCreateJob: boolean;
  public hasManageActions: boolean;
  public canDeleteOpeningsAndUnassign: boolean;
  public isEditingOpenings: boolean;
  public isCurrentUserApproverInAnyStep: boolean;
  public currentUserActiveApproverDecisionId: number | null;

  private formDataAdapter = new RequisitionFormDataAdapter();
  private currentUserId: number;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private requisitionsActionsService: SingleRequisitionsActionsService,
    private requisitionsNotificationsService: RequisitionsNotificationsService,
    private requisitionsSharedActionsService: RequisitionsSharedActionsService,
    private requisitionsGeneralFacade: RequisitionsGeneralFacade,
    private requisitionFormManager: RequisitionFormManagerService,
    private requisitionSave: RequisitionSaveService,
    private createJobFacade: CreateJobFacade,
    private store: Store,
    private cdRef: ChangeDetectorRef,
  ) {
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(currentUser => {
        this.showFeatureDiscoveryVideoInfobox =
          !currentUser.featureDiscovery[this.featureDiscoveryKey];
        this.currentUserId = currentUser.id;
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.requisition?.currentValue) {
      this.handleRequisitionUpdate(changes.requisition);
      this.handleRequestApprovalDisabled();
    }

    if (changes.allApproversDeleted || changes.approversWithoutAccessCount) {
      this.handleRequestApprovalDisabled();
    }

    if (changes.approvalFlow) {
      this.handleApproverState();
    }
  }

  public ngOnInit(): void {
    this.form.valueChanges
      .pipe(debounceTime(10), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public get openingsFormArray() {
    return this.requisitionFormManager.openingsFormArray;
  }

  public get availableFormFields(): RequisitionDetailsFormFields[] {
    return this.requisition.form.fields;
  }

  public get isCurrentUserApproverInActiveStep(): boolean {
    return (
      !!this.currentUserActiveApproverDecisionId &&
      this.requisition.status === RequisitionStatus.Pending
    );
  }

  public get showApprovalRequestMessage(): boolean {
    return (
      this.requisition.status === RequisitionStatus.Pending &&
      this.isCurrentUserApproverInAnyStep &&
      (!!this.requisition.approvalRequest?.attachments?.length ||
        !!this.requisition.approvalRequest?.bodyHtml)
    );
  }

  public get form(): FormGroup<RequisitionForm> {
    return this.requisitionFormManager.form;
  }

  public save(): void {
    this.requisitionSave
      .save(false, UpdateContext.Default)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe();
  }

  public forceOpeningsDelete(): void {
    const deletedOpeningsCount =
      this.savedOpenings.length - this.form.get('openings')!.value!.length;
    if (deletedOpeningsCount) {
      this.requisitionsActionsService.deleteOpenings(
        {
          openings: [...this.form.get('openings')!.getRawValue()],
          id: this.requisition.id,
        },
        deletedOpeningsCount,
      );
    }
  }

  public createJob(): void {
    this.requisitionsSharedActionsService.createJob(this.requisition.id);
  }

  public unassignCandidate(openingId: number): void {
    this.requisitionsActionsService.unassignCandidate(openingId, this.requisition.id);
  }

  public requestApproval(): void {
    const isChangedRequisitionFields = this.requisitionFormManager.hasRequisitionFieldsChanged(
      this.requisition,
    );
    this.requisitionFormManager.toggleFieldsRequired();
    this.requisitionFormManager.keepValidation(true);
    const isValidFormBeforeUpdate = this.requisitionFormManager.form.valid;
    this.requisitionSave
      .save(false, UpdateContext.ApprovalRequest)
      .pipe(take(1), filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        if (this.form.valid) {
          this.requisitionFormManager.keepValidation(false);
          return this.requisitionsActionsService.requestApproval(this.requisition);
        }

        if (isChangedRequisitionFields && isValidFormBeforeUpdate)
          return this.requisitionsNotificationsService.requisitionFieldsChanged();
        this.requisitionsNotificationsService.fillRequiredFields();
      });
  }

  public cancelRequest(): void {
    this.requisitionsSharedActionsService.cancelApprovalRequest(this.requisition.id);
  }

  public approveRequest(): void {
    this.requisitionsActionsService.approveRequest(
      this.requisition.id,
      this.currentUserActiveApproverDecisionId!,
      this.findCurrentApprovalStep(),
    );
  }

  public rejectRequest(): void {
    this.requisitionsActionsService.rejectRequest(
      this.requisition.id,
      this.currentUserActiveApproverDecisionId!,
      this.findCurrentApprovalStep(),
    );
  }

  public duplicate(): void {
    this.requisitionsSharedActionsService.duplicate(this.requisition.id);
  }

  public archive(): void {
    this.requisitionsSharedActionsService.archive(this.requisition);
  }

  public retrieve(): void {
    this.requisitionsSharedActionsService.retrieve(this.requisition);
  }

  public delete(): void {
    this.requisitionsSharedActionsService.delete(this.requisition, true);
  }

  public isFormBoxVisible(formName: FormBox): boolean {
    return !!Object.keys(this.availableFieldsMap[formName]).length;
  }

  public setIsEditingOpening(editing: boolean): void {
    this.isEditingOpenings = editing;
    this.cdRef.detectChanges();
  }

  private handleRequisitionUpdate(requisition: SimpleChange) {
    const isEditingRequisition = this.form ? !this.form.pristine : false;
    const isChangedRequisitionStatus = this.requisitionFormManager.hasRequisitionStatusChanged(
      requisition.currentValue,
    );
    if (isEditingRequisition && !isChangedRequisitionStatus && !requisition.firstChange) return;

    this.isFormEditable = this.requisitionsGeneralFacade.isFormEditable;
    this.hasManageActions = this.requisitionsGeneralFacade.hasManageRequisitionActions;
    this.canDeleteOpeningsAndUnassign = this.requisitionsGeneralFacade.canDeleteOpeningsAndUnassign;
    this.canCreateJob = this.createJobFacade.canCreateJob;
    this.savedOpenings = this.requisition.openings;
    this.deletedOpeningsCandidatesCount = this.requisition.openingsWithDeletedCandidateCount;
    this.availableFieldsMap = this.formDataAdapter.getAvailableFields(this.availableFormFields);

    this.handleFillForm(requisition);
  }

  private handleFillForm(requisition: SimpleChange) {
    if (requisition?.firstChange) {
      !this.form
        ? this.requisitionFormManager.initializeForm()
        : this.requisitionFormManager.updateForm();
      return;
    }

    const { title: previousTitle, ...previousRestValue } = requisition?.previousValue;
    const { title: currentTitle, ...currentRestValue } = requisition?.currentValue;
    const onlyTitleChanged =
      previousTitle !== currentTitle && deepEqual(previousRestValue, currentRestValue);
    if (onlyTitleChanged) return;

    if (this.requisitionFormManager.hasRequisitionFieldsChanged(requisition.currentValue)) {
      this.requisitionFormManager.updateForm();
      return;
    }

    this.requisitionFormManager.fillForm();
  }

  private handleRequestApprovalDisabled(): void {
    this.requestApprovalDisabled = this.requisitionsGeneralFacade.getRequestApprovalDisabledState(
      this.allApproversDeleted,
    );
  }

  private handleApproverState(): void {
    this.isCurrentUserApproverInAnyStep = !!this.approvalFlow?.steps.some(step =>
      step.approverIds.some(id => this.currentUserId === id),
    );
    const currentApprovalStep = this.approvalFlow?.steps.find(step => step.status === 'current');

    this.currentUserActiveApproverDecisionId =
      currentApprovalStep?.approverDecisions?.find(
        approverDecision =>
          this.currentUserId === approverDecision.approverId &&
          approverDecision.status === DecisionStatus.Requested,
      )?.id || null;
  }

  private findCurrentApprovalStep(): number {
    return this.approvalFlow?.steps.findIndex(step => step.status === 'current')! + 1;
  }
}
